import React, { useEffect, useState, useRef } from 'react';
import { FilterBar } from './FilterBar';
import { FilterModal } from './FilterModal';
import { ActiveFilter } from '../types/filters';
import { useViewMode } from '../context/ViewModeContext';
import { WalletBadge } from './billing/WalletBadge';
import { MapPinIcon, ChevronDownIcon, CheckIcon, BuildingIcon } from 'lucide-react';
function LocationsDropdown() {
  const {
    locations,
    selectedBrandLocations,
    setSelectedBrandLocations,
    isAllLocationsSelected,
    getSelectedLocationData
  } = useViewMode();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleLocationToggle = (locationCode: string) => {
    if (locationCode === 'all') {
      setSelectedBrandLocations(['all']);
    } else {
      setSelectedBrandLocations((prev: string[]) => {
        const withoutAll = prev.filter(code => code !== 'all');
        if (withoutAll.includes(locationCode)) {
          const newSelection = withoutAll.filter(code => code !== locationCode);
          return newSelection.length === 0 ? ['all'] : newSelection;
        } else {
          // For single location selection, replace the current selection
          return [locationCode];
        }
      });
    }
  };
  const getDisplayText = () => {
    if (isAllLocationsSelected) return 'All Locations';
    if (selectedBrandLocations.length === 1) {
      const loc = locations.find(l => l.code === selectedBrandLocations[0]);
      return loc?.name || 'Select Location';
    }
    return `${selectedBrandLocations.length} Locations`;
  };
  const selectedLocation = getSelectedLocationData();
  return <div className="relative" ref={dropdownRef} data-id="element-1203">
      <button onClick={() => setIsOpen(!isOpen)} className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg transition-all ${isAllLocationsSelected ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:from-blue-100 hover:to-indigo-100' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 hover:from-amber-100 hover:to-orange-100'}`} data-id="element-1204">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isAllLocationsSelected ? 'bg-blue-500' : 'bg-amber-500'}`} data-id="element-1205">
          <MapPinIcon size={14} className="text-white" data-id="element-1206" />
        </div>
        <div className="text-left" data-id="element-1207">
          <p className={`text-[10px] font-medium leading-none ${isAllLocationsSelected ? 'text-blue-600' : 'text-amber-600'}`} data-id="element-1208">
            {isAllLocationsSelected ? 'Viewing' : 'Filtered to'}
          </p>
          <p className={`text-sm font-bold ${isAllLocationsSelected ? 'text-blue-800' : 'text-amber-800'}`} data-id="element-1209">
            {getDisplayText()}
          </p>
        </div>
        <ChevronDownIcon size={16} className={`transition-transform ${isAllLocationsSelected ? 'text-blue-500' : 'text-amber-500'} ${isOpen ? 'rotate-180' : ''}`} data-id="element-1210" />
      </button>

      {isOpen && <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-1211">
          <div className="p-3 border-b border-gray-100 bg-gray-50" data-id="element-1212">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-1213">
              Filter by Location
            </p>
          </div>

          {/* All Locations Option */}
          <div onClick={() => handleLocationToggle('all')} className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b border-gray-100 ${isAllLocationsSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1214">
            <div className="flex items-center gap-3" data-id="element-1215">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isAllLocationsSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`} data-id="element-1216">
                {isAllLocationsSelected && <CheckIcon size={12} className="text-white" data-id="element-1217" />}
              </div>
              <div className="flex items-center gap-2" data-id="element-1218">
                <BuildingIcon size={16} className="text-gray-400" data-id="element-1219" />
                <div data-id="element-1220">
                  <span className="text-sm font-medium text-gray-900" data-id="element-1221">
                    All Locations
                  </span>
                  <p className="text-xs text-gray-500" data-id="element-1222">
                    Aggregated view across all locations
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="px-4 py-2 bg-gray-50" data-id="element-1223">
            <p className="text-xs text-gray-500" data-id="element-1224">
              Or select a single location:
            </p>
          </div>

          {/* Individual Locations */}
          <div className="max-h-64 overflow-y-auto" data-id="element-1225">
            {locations.map(location => {
          const isSelected = !isAllLocationsSelected && selectedBrandLocations.includes(location.code);
          return <div key={location.code} onClick={() => handleLocationToggle(location.code)} className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b border-gray-50 last:border-0 ${isSelected ? 'bg-amber-50' : 'hover:bg-gray-50'}`} data-id="element-1226">
                  <div className="flex items-center gap-3" data-id="element-1227">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`} data-id="element-1228">
                      {isSelected && <CheckIcon size={12} className="text-white" data-id="element-1229" />}
                    </div>
                    <div data-id="element-1230">
                      <p className="text-sm font-medium text-gray-900" data-id="element-1231">
                        {location.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1232">
                        {location.code} • {location.city}
                      </p>
                    </div>
                  </div>
                </div>;
        })}
          </div>

          <div className="p-3 border-t border-gray-100 bg-gray-50" data-id="element-1233">
            <button onClick={() => setIsOpen(false)} className={`w-full py-2 text-white rounded-lg text-sm font-medium transition-colors ${isAllLocationsSelected ? 'bg-blue-500 hover:bg-blue-600' : 'bg-amber-500 hover:bg-amber-600'}`} data-id="element-1234">
              Apply Filter
            </button>
          </div>
        </div>}
    </div>;
}
export function Header() {
  const {
    viewMode,
    currentLocation
  } = useViewMode();
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const handleRemoveFilter = (filterId: string) => {
    setActiveFilters(prev => prev.filter(f => f.filterId !== filterId));
  };
  const handleOpenFilters = () => {
    setIsFilterModalOpen(true);
  };
  const handleApplyFilters = (filters: ActiveFilter[]) => {
    setActiveFilters(filters);
  };
  return <>
      <header className="border-b border-gray-200 px-6 py-3 bg-white" data-id="element-1235">
        <div className="flex items-center justify-between" data-id="element-1236">
          {/* Left side - empty spacer */}
          <div className="flex-1" data-id="element-1237" />

          {/* Right side */}
          <div className="flex items-center gap-4" data-id="element-1238">
            {/* Show Locations Dropdown for Brand View */}
            {viewMode === 'brand' && <LocationsDropdown data-id="element-1239" />}

            {/* Show Wallet Badge for Store View */}
            {viewMode === 'store' && <WalletBadge data-id="element-1240" />}

            <FilterBar activeFilters={activeFilters} onRemoveFilter={handleRemoveFilter} onOpenFilters={handleOpenFilters} data-id="element-1241" />
          </div>
        </div>
      </header>

      <FilterModal isOpen={isFilterModalOpen} onClose={() => setIsFilterModalOpen(false)} activeFilters={activeFilters} onApplyFilters={handleApplyFilters} data-id="element-1242" />
    </>;
}